import { bookingStatusLabel } from "@/lib/bookingStatus";
import type { Booking } from "@/types/camping";

/** Vereinigt Buchungsliste mit offenen Anfragen (dedupliziert nach ID). */
export function mergeBookingsWithInquiries(bookings: Booking[], inquiries: Booking[]): Booking[] {
  const byId = new Map<string, Booking>();
  for (const booking of bookings) byId.set(booking.id, booking);
  for (const inquiry of inquiries) byId.set(inquiry.id, inquiry);
  return [...byId.values()];
}

export type BookingSortKey = "booking_number" | "guest" | "pitch" | "arrival" | "status" | "total";
export type SortDir = "asc" | "desc";

export interface BookingSearchContext {
  guestName: string;
  pitchLabel: string;
}

export function matchesBookingSearch(
  booking: Booking,
  query: string,
  { guestName, pitchLabel }: BookingSearchContext,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const statusLabel = bookingStatusLabel(booking.status).toLowerCase();
  return (
    booking.booking_number.toLowerCase().includes(q) ||
    guestName.toLowerCase().includes(q) ||
    pitchLabel.toLowerCase().includes(q) ||
    (booking.license_plate ?? "").toLowerCase().includes(q) ||
    statusLabel.includes(q) ||
    booking.arrival_date.includes(q) ||
    booking.departure_date.includes(q)
  );
}

function compareStrings(a: string, b: string, dir: SortDir): number {
  const result = a.localeCompare(b, "de", { sensitivity: "base", numeric: true });
  return dir === "asc" ? result : -result;
}

function compareNumbers(a: number, b: number, dir: SortDir): number {
  const result = a - b;
  return dir === "asc" ? result : -result;
}

export function sortBookings(
  bookings: Booking[],
  sortKey: BookingSortKey,
  sortDir: SortDir,
  getGuestName: (booking: Booking) => string,
  getPitchLabel: (booking: Booking) => string,
): Booking[] {
  const sorted = [...bookings];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "booking_number":
        cmp = compareStrings(a.booking_number, b.booking_number, sortDir);
        break;
      case "guest":
        cmp = compareStrings(getGuestName(a), getGuestName(b), sortDir);
        break;
      case "pitch":
        cmp = compareStrings(getPitchLabel(a), getPitchLabel(b), sortDir);
        break;
      case "arrival":
        cmp = compareStrings(a.arrival_date, b.arrival_date, sortDir);
        break;
      case "status":
        cmp = compareStrings(
          bookingStatusLabel(a.status),
          bookingStatusLabel(b.status),
          sortDir,
        );
        break;
      case "total":
        cmp = compareNumbers(
          Number.parseFloat(a.total_amount) || 0,
          Number.parseFloat(b.total_amount) || 0,
          sortDir,
        );
        break;
    }
    if (cmp !== 0) return cmp;
    return compareStrings(a.booking_number, b.booking_number, "asc");
  });
  return sorted;
}

export function nextSortState(
  currentKey: BookingSortKey,
  currentDir: SortDir,
  clickedKey: BookingSortKey,
): { key: BookingSortKey; dir: SortDir } {
  if (currentKey === clickedKey) {
    return { key: clickedKey, dir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { key: clickedKey, dir: "asc" };
}
